import { useCallback } from 'react';

import { useParams } from 'react-router';

import { usePromiseSnackbar } from '@/hooks/usePromiseSnackbar';
import { MatchParams } from '@/pages/SharedEndpoint/types';
import { namedOperations as workspaceModelAPIsNamedOperations } from '@/pages/Workspace/ModelAPIs/__generated__/queries.generated';
import { WorkspaceSections } from '@/pages/Workspace/types';
import { BasetenPageEnum, usePageSwitcher } from '@/routes';
import useRegisterBreadcrumbs from '@/sections/Billip/BillBar/useRegisterBreadcrumbs';
import useSafeguardConfirmationDialog from '@/sections/Dialog/SafeguardConfirmation';

import {
  useCreateOrgSharedEndpointMutation,
  useDeleteOrgSharedEndpointMutation,
} from './__generated__/mutations.generated';
import {
  namedOperations,
  useOrgSharedEndpointQuery,
  useOrgSharedEndpointsQuery,
  useSharedEndpointsQuery,
} from './__generated__/queries.generated';

function useSharedEndpoints() {
  const { data, loading } = useSharedEndpointsQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    sharedEndpoints: data?.sharedEndpoints,
    loading,
  };
}

function useOrgSharedEndpoints() {
  const { data, loading } = useOrgSharedEndpointsQuery({
    fetchPolicy: 'cache-first',
  });

  return {
    orgSharedEndpoints: data?.organizationSharedEndpoints,
    loading,
  };
}

function useOrgSharedEndpoint(poll = false) {
  const { sharedEndpointId } = useParams<MatchParams>();

  const { data, loading } = useOrgSharedEndpointQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      id: sharedEndpointId,
    },
    pollInterval: poll ? 10000 : null,
  });

  return {
    orgSharedEndpoint: data?.organizationSharedEndpoint,
    loading,
  };
}

function useCreateOrgSharedEndpoint() {
  const [createOrgSharedEndpoint, { loading, error, data }] = useCreateOrgSharedEndpointMutation();

  return {
    createOrgSharedEndpoint,
    loading,
    error,
    createdOrgSharedEndpoint: data?.createOrgSharedEndpoint,
  };
}

function useDeleteOrgSharedEndpoint(name: string, id: string) {
  const promiseSnackbar = usePromiseSnackbar();
  const goToPage = usePageSwitcher();
  const safeguardConfirmationDialog = useSafeguardConfirmationDialog();

  const [deleteOrgSharedEndpoint] = useDeleteOrgSharedEndpointMutation();

  return useCallback(async () => {
    const failedText = `An unexpected error occurred. Model API ${name} could not be deleted.`;
    const variables = {
      orgSharedEndpointId: id,
    };

    const confirmed = await safeguardConfirmationDialog({
      title: 'Delete Model API',
      okText: 'Delete',
      prompt: 'Are you sure you want to delete this Model API? This cannot be undone.',
      validationText: name,
      validationHint: 'Model API name',
    });

    if (confirmed) {
      promiseSnackbar(
        deleteOrgSharedEndpoint({
          variables,
          onCompleted() {
            goToPage(BasetenPageEnum.Workspace, {
              section: WorkspaceSections.Overview,
            });
          },
          refetchQueries: [
            namedOperations.Query.OrgSharedEndpoints,
            workspaceModelAPIsNamedOperations.Query.WorkspaceOrgSharedEndpoints,
          ],
        }),
        'The Model API was successfully deleted.',
        failedText,
      );
    }
  }, [deleteOrgSharedEndpoint, goToPage, name, promiseSnackbar, id, safeguardConfirmationDialog]);
}

function useRegisterSharedEndpointBreadcrumbs() {
  const { sharedEndpointId } = useParams<MatchParams>();
  const { orgSharedEndpoint } = useOrgSharedEndpoint();
  useRegisterBreadcrumbs([
    {
      entityType: 'SharedEndpoint',
      id: orgSharedEndpoint?.sharedEndpoint.id,
    },
    {
      entityType: 'OrganizationSharedEndpoint',
      id: sharedEndpointId,
    },
  ]);
}

export {
  useSharedEndpoints,
  useOrgSharedEndpoint,
  useCreateOrgSharedEndpoint,
  useOrgSharedEndpoints,
  useDeleteOrgSharedEndpoint,
  useRegisterSharedEndpointBreadcrumbs,
};
